import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Compass, ArrowLeft, BookOpen } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = {
  home_care: "Home Care",
  legal_financial: "Legal & Financial",
  emotional_support: "Emotional Support",
  medical_care: "Medical Care"
};

export default function ArticlePage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      const foundArticle = response.data.find(a => a.id === articleId);
      if (foundArticle) {
        setArticle(foundArticle);
      } else {
        toast.error("Article not found");
        navigate("/library");
      }
    } catch (error) {
      console.error("Error loading article:", error);
      toast.error("Failed to load article");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <p className="text-lg text-text-secondary font-body">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-library-button"
              onClick={() => navigate("/library")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={2} />
              <h1 className="text-xl font-heading font-bold text-primary">Article</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-8 md:p-12">
          <div className="mb-6">
            <span className="px-3 py-1 bg-accent/10 text-accent text-sm font-body font-semibold rounded-full">
              {CATEGORIES[article.category] || article.category}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-[#E2E8F0]">
            <div className="text-sm text-text-secondary font-body">
              <span className="font-semibold">By {article.author_name}</span>
              {article.author_type === "provider" && (
                <span className="ml-2 px-2 py-0.5 bg-secondary/10 text-secondary text-xs rounded">Provider</span>
              )}
            </div>
            <span className="text-sm text-text-secondary font-body">
              {new Date(article.published_at || article.created_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric"
              })}
            </span>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="text-base leading-relaxed text-text-primary font-body whitespace-pre-wrap">
              {article.content}
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
